import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ArrowRight, X, Filter } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import PageHero from "@/components/shared/PageHero";
import api from "@/lib/api";

export default function Products() {
  const [items, setItems] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  const [q, setQ] = useState(searchParams.get("q") || "");
  const [category, setCategory] = useState(searchParams.get("category") || "all");
  const [brand, setBrand] = useState(searchParams.get("brand") || "all");
  const [gender, setGender] = useState(searchParams.get("gender") || "all");

  useEffect(() => {
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (category && category !== "all") params.category = category;
    if (brand && brand !== "all") params.brand = brand;
    if (gender && gender !== "all") params.gender = gender;
    api.get("/products", { params })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));

    const sp = {};
    if (q) sp.q = q;
    if (category && category !== "all") sp.category = category;
    if (brand && brand !== "all") sp.brand = brand;
    if (gender && gender !== "all") sp.gender = gender;
    setSearchParams(sp, { replace: true });
  }, [q, category, brand, gender, setSearchParams]);

  const clearFilters = () => { setQ(""); setCategory("all"); setBrand("all"); setGender("all"); };

  const filtersActive = useMemo(() =>
    (q || category !== "all" || brand !== "all" || gender !== "all"),
  [q, category, brand, gender]);

  return (
    <div>
      <PageHero
        label="Wholesale Catalogue"
        title="Curated products for bulk supply."
        subtitle="Explore our multi-brand wholesale portfolio. Every product is available for bulk orders with clear MOQ and B2B pricing. This is a catalogue — pricing is disclosed on quotation."
        breadcrumbs={[{ label: "Products" }]}
      />

      <section className="py-10 border-b border-border/60 bg-secondary/30">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="grid md:grid-cols-12 gap-3 items-center">
            <div className="md:col-span-4 relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/50" />
              <Input
                data-testid="products-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search products, brands…"
                className="pl-10 h-11 rounded-sm bg-card"
              />
            </div>
            <div className="md:col-span-2">
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="products-filter-category" className="h-11 rounded-sm bg-card">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Select value={brand} onValueChange={setBrand}>
                <SelectTrigger data-testid="products-filter-brand" className="h-11 rounded-sm bg-card">
                  <SelectValue placeholder="Brand" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Brands</SelectItem>
                  {brands.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Select value={gender} onValueChange={setGender}>
                <SelectTrigger data-testid="products-filter-gender" className="h-11 rounded-sm bg-card">
                  <SelectValue placeholder="Gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="Men">Men</SelectItem>
                  <SelectItem value="Women">Women</SelectItem>
                  <SelectItem value="Kids">Kids</SelectItem>
                  <SelectItem value="Unisex">Unisex</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 flex gap-2">
              {filtersActive && (
                <Button variant="outline" onClick={clearFilters} className="h-11 rounded-sm gap-2 border-border/60">
                  <X size={14} /> Clear
                </Button>
              )}
              <div className="h-11 px-3 rounded-sm border border-border/60 bg-card flex items-center gap-2 text-xs tracking-wider uppercase text-foreground/70 flex-1 justify-center">
                <Filter size={13} /> {items.length} results
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="aspect-[4/5] rounded-md bg-secondary animate-pulse" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-24">
              <p className="section-label mb-3">No matches</p>
              <p className="font-serif text-3xl text-foreground">Nothing found for your filters.</p>
              <p className="mt-3 text-foreground/60">Try clearing filters or contact our sales team for a curated catalogue.</p>
              <div className="mt-8">
                <Link to="/request-catalogue">
                  <Button className="rounded-sm h-11 bg-gold hover:bg-gold-400 text-navy-900 font-semibold tracking-wider uppercase text-xs px-6">Request Catalogue</Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {items.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: i * 0.03 }}
                >
                  <Link to={`/products/${p.slug}`} className="group block relative overflow-hidden rounded-md border border-border/60 hover:border-gold/60 bg-card transition-colors">
                    <div className="aspect-[4/5] overflow-hidden bg-secondary">
                      {p.images?.[0] ? (
                        <img src={p.images[0]} alt={p.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-foreground/30 text-xs">No Image</div>
                      )}
                      {p.featured && <span className="absolute top-3 left-3 text-[0.6rem] tracking-widest uppercase px-2 py-1 bg-gold/90 text-navy-900 font-semibold">Featured</span>}
                      {p.new_arrival && <span className="absolute top-3 right-3 text-[0.6rem] tracking-widest uppercase px-2 py-1 bg-navy/80 text-gold font-semibold border border-gold/40">New</span>}
                    </div>
                    <div className="p-5">
                      <p className="section-label text-gold text-[0.65rem]">{p.brand || "Multi Brand"}</p>
                      <h4 className="mt-2 font-serif text-lg text-foreground group-hover:text-gold transition-colors line-clamp-1">{p.name}</h4>
                      <div className="mt-3 flex items-center justify-between text-xs text-foreground/60">
                        <span>MOQ: {p.moq}+ pcs</span>
                        <span className="text-gold flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
                          Enquire <ArrowRight size={12} />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
